/**
 * WebGPU canvas surface lifecycle.
 *
 * This is where the "A valid external Instance reference no longer exists" class
 * of crash is prevented, once. A WebGPU swap-chain surface does not survive being
 * configured-but-idle across a resize; the guards here keep it healthy:
 *
 *  - `getCurrentTexture()` is always acquired fresh (never cached across frames).
 *  - `resize()` reconfigures the context (DPR-clamped) whenever the size changes.
 *  - `reconfigure()` re-arms the context when an effect resumes after being
 *    parked off-screen, so the next frame draws to a valid surface.
 *  - `keepAlive()` does a minimal clear-present so a parked effect's surface
 *    never goes stale — this is what lets the frame loop skip *work* without
 *    losing the *surface*.
 */

export interface WebGPUSurface {
  readonly ctx: GPUCanvasContext
  readonly format: GPUTextureFormat
  /** Backing pixel size (already DPR-scaled). */
  readonly width: number
  readonly height: number
  /** Fresh current-texture for this frame. Throws only if the surface is gone. */
  getCurrentTexture(): GPUTexture
  /** Re-apply configuration to the context (idempotent). */
  reconfigure(): void
  /** Resize to CSS size × dpr; returns true if the backing size changed. */
  resize(cssWidth: number, cssHeight: number, dpr: number): boolean
  /** Minimal clear present to keep a parked surface valid. */
  keepAlive(): void
  dispose(): void
}

const MAX_DPR = 2

export function createWebGPUSurface(
  canvas: HTMLCanvasElement,
  device: GPUDevice,
  format: GPUTextureFormat,
  alphaMode: GPUCanvasAlphaMode = "premultiplied"
): WebGPUSurface | null {
  const ctx = canvas.getContext("webgpu") as GPUCanvasContext | null
  if (!ctx) return null

  let width = Math.max(1, canvas.width)
  let height = Math.max(1, canvas.height)

  const configure = () => {
    ctx.configure({ device, format, alphaMode })
  }
  configure()

  return {
    ctx,
    format,
    get width() {
      return width
    },
    get height() {
      return height
    },

    getCurrentTexture() {
      return ctx.getCurrentTexture()
    },

    reconfigure() {
      configure()
    },

    resize(cssWidth, cssHeight, dpr) {
      const scale = Math.min(dpr || 1, MAX_DPR)
      const w = Math.max(1, Math.round(cssWidth * scale))
      const h = Math.max(1, Math.round(cssHeight * scale))
      if (w === width && h === height) return false
      canvas.width = w
      canvas.height = h
      width = w
      height = h
      // Reconfiguring after a resize is the specific step that prevents the
      // stale-surface device loss.
      configure()
      return true
    },

    keepAlive() {
      const view = ctx.getCurrentTexture().createView()
      const enc = device.createCommandEncoder()
      const pass = enc.beginRenderPass({
        colorAttachments: [
          { view, clearValue: { r: 0, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" },
        ],
      })
      pass.end()
      device.queue.submit([enc.finish()])
    },

    dispose() {
      try {
        ctx.unconfigure()
      } catch {
        /* already gone */
      }
    },
  }
}
